import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Typography, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  LANGUAGES,
  buildPathWithLocale,
  setStoredLocale,
} from '../../locales/languages';

function LanguageSelector({ currentLocale, onChange, compact, showTitle }) {
  const { i18n, t } = useTranslation();
  const activeLocale = currentLocale || i18n.language || 'en';
  const normalizedLocale = activeLocale.split('-')[0];
  const gridItemProps = compact ? { xs: 12 } : { xs: 12, sm: 6, md: 4 };
  const handleSelect = (code) => {
    setStoredLocale(code);
    if (onChange) {
      onChange(code);
      return;
    }
    i18n.changeLanguage(code);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.pathname = buildPathWithLocale(url.pathname, code);
      window.history.replaceState({}, '', url);
    }
  };

  return (
    <Box>
      {showTitle && (
        <Typography variant="h6" sx={{ mb: compact ? 1.5 : 2 }}>
          {t('languageSelector.title')}
        </Typography>
      )}

      <Grid container spacing={compact ? 1 : 2}>
        {LANGUAGES.map((lang) => {
          const isActive = lang.code === normalizedLocale;

          return (
            <Grid item {...gridItemProps} key={lang.code}>
              <Paper
                onClick={() => handleSelect(lang.code)}
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  px: compact ? 1.5 : 2,
                  py: compact ? 1.25 : 1.5,
                  borderRadius: compact ? 1.5 : 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: compact ? 1 : 1.5,
                  border: '1px solid',
                  borderColor: isActive ? 'primary.main' : 'divider',
                  bgcolor: isActive ? 'primary.main' : 'transparent',
                  color: isActive ? 'primary.contrastText' : 'text.primary',
                  transition: 'all 0.2s ease',
                  minWidth: 0,
                  '&:hover': {
                    bgcolor: isActive ? 'primary.dark' : 'action.hover',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Box sx={{ fontSize: compact ? 18 : 22 }}>{lang.flag}</Box>

                <Typography
                  sx={{
                    fontWeight: isActive ? 600 : 500,
                    fontSize: compact ? 14 : 16,
                    lineHeight: 1.2,
                  }}
                >
                  {lang.label}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

LanguageSelector.propTypes = {
  currentLocale: PropTypes.string,
  onChange: PropTypes.func,
  compact: PropTypes.bool,
  showTitle: PropTypes.bool,
};

LanguageSelector.defaultProps = {
  currentLocale: undefined,
  onChange: undefined,
  compact: false,
  showTitle: true,
};

export default LanguageSelector;
export { LANGUAGES };
